import React, { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { History, Download, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

/**
 * Phase 29C — Blocco "Precedente visita" per cartella medico.
 *
 * Chiama GET /api/medico/visits/{vid}/previous-visit e mostra:
 * - Se non disponibile: piccolo messaggio "Nessuna visita precedente disponibile".
 * - Se disponibile: card compatta con highlight (data, anamnesi breve, tono OD/OS,
 *   cristallino, fundus), + bottone "Importa tutto da precedente" con conferma.
 *
 * Props:
 *  - visitId: id visita corrente (obbligatorio)
 *  - readOnly: se true nasconde il bottone import
 *  - onImport: callback(shape) → riceve lo shape della precedente visita per
 *    aggiornare stato parent (anamnesi/OD/OS/diagnosi/terapia/protocollo).
 */
export default function PreviousVisitBlock({ visitId, readOnly, onImport }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null); // { available, shape, message }
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    let mounted = true;
    if (!visitId) { setLoading(false); return; }
    (async () => {
      setLoading(true);
      try {
        const { data: d } = await api.get(`/medico/visits/${visitId}/previous-visit`);
        if (mounted) setData(d);
      } catch (e) {
        if (mounted) setData({ available: false, shape: null, message: apiErr(e, "Errore caricamento precedente visita") });
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [visitId]);

  // Import selettivo: il medico sceglie cosa importare (tutto / anamnesi / refrazione / esame / terapia)
  const [parts, setParts] = useState({ anamnesi: true, refrazione: true, esame: true, terapia: true });
  const togglePart = (k) => setParts((p) => ({ ...p, [k]: !p[k] }));

  const doImport = () => {
    if (!data?.shape) return;
    setImporting(true);
    try {
      onImport?.(data.shape, parts);
      toast.success("Dati precedente visita importati (modificabili prima del salvataggio)");
      setConfirmOpen(false);
    } catch (e) {
      toast.error("Errore importazione: " + (e?.message || "sconosciuto"));
    } finally {
      setImporting(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-[#E2E8F0] p-3 mb-4 flex items-center gap-2 text-sm text-slate-500" data-testid="prev-visit-loading">
        <Loader2 className="w-4 h-4 animate-spin" /> Caricamento precedente visita…
      </div>
    );
  }

  if (!data?.available) {
    return (
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-3 mb-4 flex items-center gap-2 text-sm text-slate-600" data-testid="prev-visit-none">
        <AlertCircle className="w-4 h-4 text-slate-400" />
        <span>{data?.message || "Nessuna visita precedente disponibile"}</span>
      </div>
    );
  }

  const s = data.shape || {};
  const prevAt = s.previous_visit_at;
  const prevDate = prevAt ? new Date(prevAt).toLocaleDateString("it-IT") : "—";
  const trunc = (t, n = 90) => {
    if (!t) return "—";
    const str = String(t).trim();
    return str.length > n ? str.slice(0, n) + "…" : str;
  };
  const od = s.OD || {};
  const os = s.OS || {};

  return (
    <>
      <div className="bg-gradient-to-r from-sky-50 to-white rounded-xl border border-sky-200 p-4 mb-4" data-testid="prev-visit-block">
        <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
          <div className="flex items-center gap-2">
            <History className="w-4 h-4 text-sky-700" />
            <div className="text-xs font-bold uppercase tracking-wider text-sky-800">
              Ultima visita chiusa · <span className="text-sky-600 normal-case">{prevDate}</span>
            </div>
          </div>
          {!readOnly && (
            <button
              type="button"
              data-testid="prev-visit-import-btn"
              onClick={() => setConfirmOpen(true)}
              className="text-xs font-bold text-white bg-brand-azure hover:bg-brand-navy px-3 py-1.5 rounded-lg inline-flex items-center gap-1.5 transition-colors"
            >
              <Download className="w-3.5 h-3.5" /> Importa tutto da ultima visita
            </button>
          )}
        </div>

        {/* Highlights compatti — non sostituiscono i campi, sono di sola lettura */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-slate-700" data-testid="prev-visit-highlights">
          {s.anamnesi && (
            <div className="col-span-full">
              <span className="font-semibold text-slate-600">Anamnesi:</span> <span className="text-slate-800">{trunc(s.anamnesi, 160)}</span>
            </div>
          )}
          <PrevFieldRow label="Tono" od={od.tono} os={os.tono} unit="mmHg" testid="prev-tono" />
          <PrevFieldRow label="Visus naturale" od={od.visus_naturale} os={os.visus_naturale} testid="prev-visus" />
          <PrevFieldRow label="Cristallino" od={od.cristallino} os={os.cristallino} testid="prev-cristallino" />
          <PrevFieldRow label="Fondo" od={trunc(od.fundus, 60)} os={trunc(os.fundus, 60)} testid="prev-fundus" />
          {s.diagnosi_precedente && (
            <div className="col-span-full">
              <span className="font-semibold text-slate-600">Diagnosi:</span> <span className="text-slate-800">{trunc(s.diagnosi_precedente, 160)}</span>
            </div>
          )}
          {s.terapia_precedente && (
            <div className="col-span-full">
              <span className="font-semibold text-slate-600">Terapia:</span> <span className="text-slate-800">{trunc(s.terapia_precedente, 160)}</span>
            </div>
          )}
        </div>
        <div className="mt-2 text-[10px] text-slate-500" data-testid="prev-visit-hint">
          Suggerimento: puoi dettare &quot;tutto invariato&quot; per copiare tutto, oppure &quot;tutto invariato tranne …&quot; per applicare solo eccezioni.
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="prev-visit-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Importare tutti i dati dell&apos;ultima visita?</AlertDialogTitle>
            <AlertDialogDescription>
              Vuoi importare tutti i dati dell&apos;ultima visita? I dati clinici (anamnesi, OD/OS,
              diagnosi, terapia, protocollo) della precedente visita del <b>{prevDate}</b> verranno
              copiati nella bozza corrente. Le modifiche correnti non salvate potrebbero essere
              sovrascritte. Puoi comunque modificare tutto prima del salvataggio finale.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="flex flex-wrap gap-2" data-testid="prev-visit-import-parts">
            {[["anamnesi", "Anamnesi"], ["refrazione", "Refrazione"], ["esame", "Esame obiettivo"], ["terapia", "Diagnosi e terapia"]].map(([k, lbl]) => (
              <label key={k} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border-2 text-xs font-bold cursor-pointer ${parts[k] ? "border-brand-azure text-brand-azure bg-brand-azure/5" : "border-slate-200 text-slate-400"}`}>
                <input type="checkbox" checked={parts[k]} onChange={() => togglePart(k)} data-testid={`prev-import-part-${k}`} className="rounded" />
                {lbl}
              </label>
            ))}
          </div>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="prev-visit-cancel-btn" disabled={importing}>Annulla</AlertDialogCancel>
            <AlertDialogAction data-testid="prev-visit-confirm-btn" disabled={importing} onClick={doImport}>
              {importing ? "Importazione…" : "Importa"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function PrevFieldRow({ label, od, os, unit, testid }) {
  const has = (v) => v !== null && v !== undefined && String(v).trim() !== "" && v !== "—";
  if (!has(od) && !has(os)) return null;
  return (
    <div className="flex items-baseline gap-1" data-testid={testid}>
      <span className="font-semibold text-slate-600">{label}:</span>
      <span className="text-slate-800">
        <span className="text-slate-500">OD</span> {has(od) ? od : "—"}{unit && has(od) ? ` ${unit}` : ""}
        <span className="mx-1 text-slate-400">·</span>
        <span className="text-slate-500">OS</span> {has(os) ? os : "—"}{unit && has(os) ? ` ${unit}` : ""}
      </span>
    </div>
  );
}
